import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeedbackForm = ({ workout, onClose, onSubmit }) => {
  const { user, currentTenant } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [difficulty, setDifficulty] = useState('');
  const [energyLevel, setEnergyLevel] = useState('');
  const [painPoints, setPainPoints] = useState('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const difficultyLevels = [
    { value: 'too_easy', label: 'Too Easy', icon: 'ThumbsDown', color: 'var(--color-success)' },
    { value: 'just_right', label: 'Just Right', icon: 'ThumbsUp', color: 'var(--color-primary)' },
    { value: 'too_hard', label: 'Too Hard', icon: 'AlertCircle', color: 'var(--color-error)' }
  ];

  const energyLevels = [
    { value: 'low', label: 'Low Energy', emoji: '😴' },
    { value: 'moderate', label: 'Moderate', emoji: '😊' },
    { value: 'high', label: 'High Energy', emoji: '🔥' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (rating === 0) {
      alert('Please provide a rating');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        ?.from('workout_feedback')
        ?.insert({
          user_id: user?.id,
          tenant_id: currentTenant?.id,
          workout_session_id: workout?.id,
          rating,
          difficulty_level: difficulty,
          energy_level: energyLevel,
          pain_points: painPoints || null,
          comments: comments || null
        });

      if (error) throw error;

      onSubmit?.();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Workout Feedback</h2>
            <p className="text-sm text-muted-foreground">{workout?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Rating */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Overall Rating *
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Icon
                    name="Star"
                    size={32}
                    className={`${
                      star <= (hoveredRating || rating)
                        ? 'fill-warning text-warning' :'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-sm text-muted-foreground">
                  {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Needs Improvement'}
                </span>
              )}
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Difficulty Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {difficultyLevels?.map((level) => (
                <button
                  key={level?.value}
                  type="button"
                  onClick={() => setDifficulty(level?.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    difficulty === level?.value
                      ? 'border-primary bg-primary/10' :'border-border hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={level?.icon} size={24} style={{ color: level?.color }} className="mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground">{level?.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Energy Level */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Energy Level During Workout
            </label>
            <div className="grid grid-cols-3 gap-3">
              {energyLevels?.map((level) => (
                <button
                  key={level?.value}
                  type="button"
                  onClick={() => setEnergyLevel(level?.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    energyLevel === level?.value
                      ? 'border-primary bg-primary/10' :'border-border hover:border-muted-foreground'
                  }`}
                >
                  <div className="text-3xl mb-2">{level?.emoji}</div>
                  <p className="text-sm font-medium text-foreground">{level?.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Pain Points */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Any Pain or Discomfort?
            </label>
            <input
              type="text"
              value={painPoints}
              onChange={(e) => setPainPoints(e?.target?.value)}
              placeholder="e.g., Lower back, left knee (optional)"
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Additional Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e?.target?.value)}
              placeholder="Share your thoughts about this workout..."
              rows={4}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center gap-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;